import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Layout effect in the browser (no frame painted at the old position); plain
// effect during prerender, where layout effects only warn.
const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/** How long to wait for a `#anchor` target that renders late (lazy pages). */
const HASH_WAIT_FRAMES = 30;

function resetScroll() {
  window.scrollTo(0, 0);
  // Inner scroll containers (the app's content pane) start at the top too.
  document.querySelectorAll<HTMLElement>("[data-scroll-root]").forEach((el) => {
    el.scrollTop = 0;
  });
}

function scrollToHash(hash: string) {
  const id = decodeURIComponent(hash.slice(1));
  let frames = 0;
  const attempt = () => {
    const target = id ? document.getElementById(id) : null;
    if (target) {
      target.scrollIntoView();
    } else if (frames++ < HASH_WAIT_FRAMES) {
      requestAnimationFrame(attempt);
    }
  };
  attempt();
}

/**
 * React Router keeps the window where it was when the path changes, so a link
 * clicked at the bottom of one page opened the next page at the bottom too.
 *
 * - A link to a new page (PUSH) starts at the top, or at its `#anchor`.
 * - Back/forward (POP) is left to the browser, which restores the old position.
 * - Replacements (redirects, the cs/en switch) keep the position.
 * - Query or hash changes on the same page (filters, tabs) keep the position.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();
  const previousPath = useRef(pathname);

  useBrowserLayoutEffect(() => {
    if (previousPath.current === pathname) return;
    previousPath.current = pathname;
    if (navigationType !== "PUSH") return;

    resetScroll();
    if (hash) scrollToHash(hash);
  }, [pathname, hash, navigationType]);

  return null;
}
